import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Calendar, CheckCircle2, MapPin, User } from 'lucide-react';
import { useState } from 'react';
import { AppLayout } from '../../components/layout/app-layout';
import { PageHeader } from '../../components/layout/page-header';
import { AppModal } from '../../components/ui/app-modal';
import { Button } from '../../components/ui/button';
import { DataTable, type DataTableColumn } from '../../components/ui/data-table';
import { FieldBox } from '../../components/ui/field-box';
import { api } from '../../lib/ky';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';

// ── Types ─────────────────────────────────────────────────────────────────────

type SiteVisitStatus =
  | 'NOT_STARTED'
  | 'CUSTOMER_APPROVAL'
  | 'APPROVED'
  | 'IN_PROGRESS'
  | 'COMPLETED';
type SiteVisitType = 'BEEAH_CONSULTANT' | 'LAB' | 'SM_CONSULTANT';

interface MySiteVisit {
  id: string;
  visitType: SiteVisitType;
  status: SiteVisitStatus;
  name: string | null;
  address: string | null;
  scheduledAt: string | null;
  completedAt: string | null;
  notes: string | null;
  rescheduledDate: string | null;
  rescheduleRemarks: string | null;
  consultant: { id: string; name: string; email: string } | null;
  request: { id: string; referenceNo: string; account: { name: string } };
}

const VISIT_TYPE_LABEL: Record<SiteVisitType, string> = {
  BEEAH_CONSULTANT: 'Beeah Consultant Site Visit',
  LAB: 'Lab Site Visit',
  SM_CONSULTANT: 'SM Consultant Site Visit',
};

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/site-visits/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('sm-site-visit:view')) throw redirect({ to: '/dashboard' });
  },
  component: MySiteVisitsPage,
});

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CFG: Record<SiteVisitStatus, { label: string; className: string }> = {
  NOT_STARTED: { label: 'Not Started', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  CUSTOMER_APPROVAL: {
    label: 'Awaiting Approval',
    className: 'bg-warning-50 text-warning-600 border-warning-200',
  },
  APPROVED: { label: 'Approved', className: 'bg-success-50 text-success-600 border-success-200' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-brand-50 text-brand-600 border-brand-200' },
  COMPLETED: { label: 'Completed', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

function StatusBadge({ status }: { status: SiteVisitStatus }) {
  const cfg = STATUS_CFG[status];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        cfg.className,
      )}
    >
      {cfg.label}
    </span>
  );
}

function fmt(iso: string | null) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

// ── Detail Modal ──────────────────────────────────────────────────────────────

function SiteVisitDetailModal({
  visit,
  onClose,
}: {
  visit: MySiteVisit;
  onClose: () => void;
}) {
  return (
    <AppModal
      open
      onClose={onClose}
      title={VISIT_TYPE_LABEL[visit.visitType]}
      description={visit.request.referenceNo}
      maxWidth="max-w-xl"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-800">{visit.name ?? 'Unnamed Visit'}</p>
          <StatusBadge status={visit.status} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FieldBox icon={<MapPin size={16} />} label="Location / Address">
            {visit.address ?? '—'}
          </FieldBox>
          <FieldBox icon={<Calendar size={16} />} label="Scheduled Date & Time">
            {fmt(visit.scheduledAt)}
          </FieldBox>
          <FieldBox icon={<User size={16} />} label="Consultant">
            {visit.consultant?.name ?? '—'}
          </FieldBox>
          <FieldBox icon={<CheckCircle2 size={16} />} label="Completed At">
            {fmt(visit.completedAt)}
          </FieldBox>
        </div>
        {visit.notes && (
          <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Notes
            </p>
            <p className="text-sm leading-relaxed text-gray-700">{visit.notes}</p>
          </div>
        )}
        {visit.rescheduledDate && (
          <div className="rounded-xl border border-warning-200 bg-warning-50 px-4 py-3">
            <p className="text-xs font-semibold text-warning-700">Customer Requested Reschedule</p>
            <p className="mt-0.5 text-xs text-warning-600">
              Preferred: {fmt(visit.rescheduledDate)}
            </p>
            {visit.rescheduleRemarks && (
              <p className="mt-0.5 text-xs italic text-warning-600">
                &ldquo;{visit.rescheduleRemarks}&rdquo;
              </p>
            )}
          </div>
        )}
        <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Study Request
          </p>
          <p className="text-sm text-gray-700">
            {visit.request.referenceNo} · {visit.request.account.name}
          </p>
        </div>
      </div>
    </AppModal>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function MySiteVisitsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [selectedVisit, setSelectedVisit] = useState<MySiteVisit | null>(null);

  const {
    data: visits = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['my-site-visits'],
    queryFn: () => api.get('site-visits/mine').json<MySiteVisit[]>(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['my-site-visits'] });

  const startMutation = useMutation({
    mutationFn: (visit: MySiteVisit) =>
      api.patch(`study-requests/${visit.request.id}/site-visits/${visit.id}/start`).json(),
    onSuccess: invalidate,
  });

  const completeMutation = useMutation({
    mutationFn: (visit: MySiteVisit) =>
      api
        .patch(`study-requests/${visit.request.id}/site-visits/${visit.id}/complete`, { json: {} })
        .json(),
    onSuccess: invalidate,
  });

  const columns: DataTableColumn<MySiteVisit>[] = [
    {
      key: 'name',
      header: 'Visit',
      render: (v) => (
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => setSelectedVisit(v)}
            className="truncate text-left font-medium text-gray-900 hover:text-brand-600 hover:underline"
          >
            {v.name ?? 'Unnamed Visit'}
          </button>
          {v.address && (
            <p className="flex items-center gap-1 text-xs text-gray-500">
              <MapPin size={11} className="shrink-0 text-gray-400" />
              {v.address}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'referenceNo',
      header: 'Study Request',
      render: (v) => (
        <button
          type="button"
          onClick={() =>
            navigate({
              to: '/study-requests/$requestId',
              params: { requestId: v.request.id },
              search: { tab: 'site-visits' },
            })
          }
          className="text-sm font-medium text-brand-600 hover:underline"
        >
          {v.request.referenceNo}
        </button>
      ),
    },
    {
      key: 'account',
      header: 'Customer',
      render: (v) => <span className="text-sm text-gray-700">{v.request.account.name}</span>,
    },
    {
      key: 'scheduledAt',
      header: 'Scheduled',
      render: (v) => (
        <span className="flex items-center gap-1.5 text-xs text-gray-500">
          <Calendar size={11} className="shrink-0 text-gray-400" />
          {fmt(v.scheduledAt)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (v) => <StatusBadge status={v.status} />,
    },
    {
      key: 'actions',
      header: '',
      render: (v) => (
        <div className="flex justify-end gap-2">
          {(v.status === 'NOT_STARTED' || v.status === 'APPROVED') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => startMutation.mutate(v)}
              disabled={startMutation.isPending}
            >
              Mark In Progress
            </Button>
          )}
          {v.status === 'IN_PROGRESS' && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => completeMutation.mutate(v)}
              disabled={completeMutation.isPending}
            >
              Mark Completed
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <AppLayout>
      <PageHeader
        title="My Site Visits"
        description="Site visits across all study requests, filtered to your consultant type."
      />
      <DataTable
        columns={columns}
        data={visits}
        keyField="id"
        isLoading={isLoading}
        isError={isError}
        emptyMessage="No site visits found"
      />
      {selectedVisit && (
        <SiteVisitDetailModal visit={selectedVisit} onClose={() => setSelectedVisit(null)} />
      )}
    </AppLayout>
  );
}
